import type { Dataset, Field } from "@/warRoom/datasets/types";
import { defineI18nGetters } from "@/warRoom/lib/i18n";
import {
  SERIES_CAP_ADJACENT,
  SERIES_CAP_ALL_PAIRS,
} from "@/warRoom/theme/palette";

import type { Aggregation, ChartType } from "./spec";

export type ChartForm = {
  type: ChartType;
  label: string;
  /** 這個型別在回答什麼問題 */
  job: string;
  /** X 軸吃什麼角色的欄位 */
  xRoles: Array<Field["role"]>;
  /** 需要 Y 量值欄位（散佈圖必填；其他可用筆數） */
  requiresMeasure: boolean;
  /** 需要數列欄位 */
  requiresSeries: boolean;
  /** 支援數列 */
  supportsSeries: boolean;
  /** 數列上限 —— 散佈圖任兩色都可能相鄰，所以只能 3 色 */
  seriesCap: number;
  /** 建議的分段上限（超過就該換型別） */
  softCategoryCap?: number;
};

const CHART_FORM_DEFS: Omit<ChartForm, "label" | "job">[] = [
  {
    type: "line",
    xRoles: ["time"],
    requiresMeasure: false,
    requiresSeries: false,
    supportsSeries: true,
    seriesCap: SERIES_CAP_ADJACENT,
  },
  {
    type: "area",
    xRoles: ["time"],
    requiresMeasure: false,
    requiresSeries: false,
    supportsSeries: false,
    seriesCap: 1,
  },
  {
    type: "column",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: false,
    supportsSeries: false,
    seriesCap: 1,
    softCategoryCap: 20,
  },
  {
    type: "bar",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: false,
    supportsSeries: false,
    seriesCap: 1,
    softCategoryCap: 30,
  },
  {
    type: "groupedColumn",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: true,
    supportsSeries: true,
    seriesCap: SERIES_CAP_ADJACENT,
  },
  {
    type: "stackedColumn",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: true,
    supportsSeries: true,
    seriesCap: SERIES_CAP_ADJACENT,
  },
  {
    type: "stackedBar",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: true,
    supportsSeries: true,
    seriesCap: SERIES_CAP_ADJACENT,
  },
  {
    type: "pie",
    xRoles: ["dimension"],
    requiresMeasure: false,
    requiresSeries: false,
    supportsSeries: false,
    seriesCap: 1,
    softCategoryCap: 6,
  },
  {
    type: "scatter",
    xRoles: ["measure"],
    requiresMeasure: true,
    requiresSeries: false,
    supportsSeries: true,
    seriesCap: SERIES_CAP_ALL_PAIRS,
  },
  {
    type: "heatmap",
    xRoles: ["dimension", "time"],
    requiresMeasure: false,
    requiresSeries: true,
    supportsSeries: true,
    seriesCap: 999,
  },
];

export const CHART_FORMS: ChartForm[] = CHART_FORM_DEFS.map(
  (d) =>
    defineI18nGetters(
      { ...d },
      {
        label: `warRoom.chartForms.${d.type}.label`,
        job: `warRoom.chartForms.${d.type}.job`,
      },
    ) as ChartForm,
);

export const formByType = (t: ChartType) =>
  CHART_FORMS.find((f) => f.type === t)!;

/** 這個資料集能不能畫這種圖 */
export function datasetSupportsForm(
  dataset: Dataset,
  form: ChartForm,
): boolean {
  const has = (role: Field["role"]) =>
    dataset.fields.some((f) => f.role === role);

  // 散佈圖的 X 軸是量值，走另一條規則
  if (form.type === "scatter") {
    return dataset.fields.filter((f) => f.role === "measure").length >= 2;
  }

  // X 軸必須有「實際可選」的欄位 —— 高基數欄位（每筆一個值）不算，
  // 拿它當 X 軸會變成一列一根柱子，那不是圖表而是表格。
  const xs = xFieldsFor(dataset, form);
  if (xs.length === 0) return false;
  if (form.requiresMeasure && !has("measure")) return false;

  // 需要數列的圖型：X 軸和數列必須能是兩個「不同」的欄位
  if (form.requiresSeries) {
    const seriesKeys = dataset.fields
      .filter((f) => f.role === "dimension" && !f.highCardinality)
      .map((f) => f.key);
    if (seriesKeys.length === 0) return false;
    const hasDistinctPair = xs.some((x) => seriesKeys.some((s) => s !== x.key));
    if (!hasDistinctPair) return false;
  }

  return true;
}

/** 這種圖有哪些資料集畫得出來 —— 給測試驗證相容規則的兩個方向一致 */
export function datasetsForForm(
  datasets: Dataset[],
  form: ChartForm,
): Dataset[] {
  return datasets.filter((d) => datasetSupportsForm(d, form));
}

/** 第 ② 步用：這份資料可以畫哪些圖 */
export function formsForDataset(dataset: Dataset): ChartForm[] {
  return CHART_FORMS.filter((f) => datasetSupportsForm(dataset, f));
}

/** 給資料集挑一個最合理的預設圖型：有時間欄位就折線，否則橫條 */
export function suggestForm(dataset: Dataset): ChartForm {
  const forms = formsForDataset(dataset);
  const hasTime = dataset.fields.some((f) => f.role === "time");
  const preferred: ChartType[] = hasTime
    ? ["line", "column", "bar"]
    : ["bar", "column", "pie"];
  for (const t of preferred) {
    const hit = forms.find((f) => f.type === t);
    if (hit) return hit;
  }
  return forms[0] ?? CHART_FORMS[2];
}

/**
 * 「建議」放 X 軸的欄位：角色對得上這種圖，而且值不會多到一列一根柱子。
 * 只用來挑預設值、決定資料集畫不畫得出這種圖 —— 不是使用者能選的上限。
 */
export function xFieldsFor(dataset: Dataset, form: ChartForm): Field[] {
  return dataset.fields.filter(
    (f) => form.xRoles.includes(f.role) && !f.highCardinality,
  );
}

/**
 * X 軸實際可選的欄位 = 這份資料的全部欄位。
 * 例外只有散佈圖：它的 X 軸在定義上就是一個量值，那是圖型本身，不是我們加的限制。
 * 建議清單以外的欄位照樣選得下去（高基數的靠「只看前 N 名」收斂），
 * UI 只負責把「建議」和「其他」分開講清楚。
 */
export function xCandidatesFor(dataset: Dataset, form: ChartForm): Field[] {
  if (form.type === "scatter") return measureFieldsFor(dataset);
  return dataset.fields;
}

/**
 * Y 軸實際可選的欄位 = 這份資料的全部欄位（散佈圖仍只吃量值，且不能跟 X 同一欄）。
 * 非量值欄位只能算「相異數」，見 aggregationsFor。
 */
export function yCandidatesFor(
  dataset: Dataset,
  form: ChartForm,
  xField: string | null,
): Field[] {
  if (form.type === "scatter") {
    return measureFieldsFor(dataset).filter((f) => f.key !== xField);
  }
  return dataset.fields;
}

/**
 * 這個 Y 欄位可以用哪些聚合。
 * 量值才有總和／平均／極值；文字、日期、布林只有「有幾個不同的值」講得通。
 * 沒選 Y 欄位就是數筆數 —— 那不是一個選項，所以清單裡只有它。
 */
export function aggregationsFor(field: Field | undefined): Aggregation[] {
  if (!field) return ["count"];
  return field.role === "measure"
    ? ["sum", "avg", "min", "max"]
    : ["countDistinct"];
}

export function measureFieldsFor(dataset: Dataset): Field[] {
  return dataset.fields.filter((f) => f.role === "measure");
}

export function seriesFieldsFor(
  dataset: Dataset,
  xField: string | null,
): Field[] {
  return dataset.fields.filter(
    (f) => f.role === "dimension" && !f.highCardinality && f.key !== xField,
  );
}
